import AppleIcon from '@mui/icons-material/Apple';
import GoogleIcon from '@mui/icons-material/Google';
import { Button, CircularProgress, Stack, Typography, useTheme } from '@mui/material';
import { useFirebase } from 'contexts/FirebaseContext';
import {
  GoogleAuthProvider,
  OAuthProvider,
  UserCredential,
  getAuth,
  onAuthStateChanged,
  signInWithCredential,
  signInWithEmailAndPassword,
  signInWithPopup,
} from 'firebase/auth';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isElectron } from 'utils/isElectron';

export interface LoginProps {
  onSuccess: (email: string, token: string | null, userCredential: UserCredential) => void;
}

export function Login(props: LoginProps) {
  const { t } = useTranslation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string>('');
  const [isWaitingForElectronLogin, setIsWaitingForElectronLogin] = useState(false);
  const [webRedirectStatus, setWebRedirectStatus] = useState<string>('');
  const theme = useTheme();

  const auth = useFirebase().auth;

  useEffect(() => {
    const unregisterAuthObserver = onAuthStateChanged(getAuth(), async (user) => {
      console.log('user', user);
    });
    return () => unregisterAuthObserver();
  }, []);

  // Handle Electron deep links
  useEffect(() => {
    if (isElectron() && window.electron) {
      // Check for any pending deep link that launched the app
      window.electron.getPendingAuth?.().then((url: string | null) => {
        if (url) {
          handleDeepLinkUrl(url);
        }
      });

      // Listen for incoming deep links while the app is running
      if (window.electron.onAuthCallback) {
        const unsubscribe = window.electron.onAuthCallback((url: string) => {
          handleDeepLinkUrl(url);
        });
        return unsubscribe;
      }
    }
  }, []);

  // Handle Web-side auto-trigger for Electron login tandem
  useEffect(() => {
    if (!isElectron()) {
      const searchParams = new URLSearchParams(window.location.search);
      const isElectronLogin = searchParams.get('electron_login') === 'true';
      const providerId = searchParams.get('provider');
      if (isElectronLogin && providerId) {
        // Auto trigger provider login
        if (providerId === 'google.com') {
          handleGoogleLogin();
        } else if (providerId === 'apple.com') {
          handleAppleLogin();
        }
      }
    }
  }, []);

  const handleDeepLinkUrl = async (urlStr: string) => {
    try {
      setIsWaitingForElectronLogin(true);
      setError('');
      const url = new URL(urlStr);
      const credentialParam = url.searchParams.get('credential');
      if (!credentialParam) {
        throw new Error('No credential parameter found in callback URL');
      }

      const credData = JSON.parse(decodeURIComponent(credentialParam));
      const { providerId, idToken, accessToken } = credData;

      let credential;
      if (providerId === 'google.com') {
        credential = GoogleAuthProvider.credential(idToken, accessToken);
      } else if (providerId === 'apple.com') {
        const appleProvider = new OAuthProvider('apple.com');
        credential = appleProvider.credential({
          idToken: idToken,
          accessToken: accessToken,
        });
      } else {
        throw new Error(`Unsupported provider: ${providerId}`);
      }

      const userCredential = await signInWithCredential(auth, credential);
      if (userCredential.user?.email) {
        const token = await userCredential.user.getIdToken();
        props.onSuccess(userCredential.user.email, token, userCredential);
      }
    } catch (err) {
      console.error('Deep link auth error:', err);
      setError(err instanceof Error ? err.message : 'Login failed from browser.');
    } finally {
      setIsWaitingForElectronLogin(false);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    try {
      const userCredential = await signInWithEmailAndPassword(auth, username, password);
      const token = await userCredential.user?.getIdToken();
      props.onSuccess(username, token, userCredential);
    } catch (error: unknown) {
      if (error instanceof Error) {
        if ('Firebase: Error (auth/invalid-email).' == error.message) {
          setError('Invalid email address. Please check your email and try again.');
        } else if ('Firebase: Error (auth/user-not-found).' == error.message) {
          setError('No user found with this email address. Please check your email and try again.');
        } else if (
          'Firebase: Error (auth/wrong-password).' == error.message ||
          'Firebase: Error (auth/invalid-login-credentials).' == error.message
        ) {
          setError('Incorrect password. Please check your password and try again.');
        } else {
          setError('Unknown error. Could not log in user');
        }
      } else {
        console.log(error);
        setError('Unknown error. Could not register user');
      }
    }
  };

  const handleProviderLogin = async (provider: OAuthProvider | GoogleAuthProvider) => {
    try {
      const userCredential = await signInWithPopup(auth, provider);

      // Check if this was an Electron login session on the web
      const searchParams = new URLSearchParams(window.location.search);
      const isElectronLogin = searchParams.get('electron_login') === 'true';

      if (isElectronLogin) {
        let cred = null;
        if (provider instanceof GoogleAuthProvider) {
          cred = GoogleAuthProvider.credentialFromResult(userCredential);
        } else {
          cred = OAuthProvider.credentialFromResult(userCredential);
        }

        if (cred) {
          const credentialData = {
            providerId: cred.providerId,
            signInMethod: cred.signInMethod,
            idToken: cred.idToken || null,
            accessToken: cred.accessToken || null,
            secret: cred.secret || null,
          };
          const deepLinkUrl = `djplayit://auth-callback?credential=${encodeURIComponent(
            JSON.stringify(credentialData)
          )}`;
          setWebRedirectStatus('Login successful! Redirecting you back to the dj-play.it app...');
          window.location.href = deepLinkUrl;
          return;
        } else {
          throw new Error('No OAuth credentials received from provider.');
        }
      }

      if (userCredential.user?.email) {
        const token = await userCredential.user.getIdToken();
        props.onSuccess(userCredential.user.email, token, userCredential);
      }
    } catch (error) {
      console.log(error);
      setError(error instanceof Error ? error.message : 'Unknown error. Could not log in user');
    }
  };

  const handleGoogleLogin = async () => {
    if (isElectron()) {
      const baseUrl = process.env.REACT_APP_BASE_URL || 'https://app.dj-play.it';
      const loginUrl = `${baseUrl}/auth?electron_login=true&provider=google.com`;
      setIsWaitingForElectronLogin(true);
      setError('');
      window.electron?.openExternal(loginUrl);
    } else {
      const provider = new GoogleAuthProvider();
      handleProviderLogin(provider);
    }
  };

  const handleAppleLogin = async () => {
    if (isElectron()) {
      const baseUrl = process.env.REACT_APP_BASE_URL || 'https://app.dj-play.it';
      const loginUrl = `${baseUrl}/auth?electron_login=true&provider=apple.com`;
      setIsWaitingForElectronLogin(true);
      setError('');
      window.electron?.openExternal(loginUrl);
    } else {
      const provider = new OAuthProvider('apple.com');
      provider.setCustomParameters({
        locale: 'en',
      });
      handleProviderLogin(provider);
    }
  };

  if (webRedirectStatus) {
    return (
      <Stack spacing={2} alignItems="center" sx={{ py: 3 }}>
        <CircularProgress color="primary" />
        <Typography variant="body1" textAlign="center">
          {webRedirectStatus}
        </Typography>
        <Typography variant="body2" color="textSecondary" textAlign="center">
          {t('youCanCloseThisWindow', 'You can safely close this browser window now.')}
        </Typography>
      </Stack>
    );
  }

  if (isWaitingForElectronLogin) {
    return (
      <Stack spacing={2} alignItems="center" sx={{ py: 3 }}>
        <CircularProgress color="primary" />
        <Typography variant="body1" textAlign="center">
          {t('waitingForBrowserLogin', 'Waiting for browser login...')}
        </Typography>
        <Typography variant="body2" color="textSecondary" textAlign="center">
          {t('pleaseCompleteLoginInBrowser', 'Please complete the login in your open web browser.')}
        </Typography>
        <Button variant="outlined" color="secondary" onClick={() => setIsWaitingForElectronLogin(false)}>
          {t('cancel', 'Cancel')}
        </Button>
        {error && <Typography color="error" variant="body2" textAlign="center">{error}</Typography>}
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      <Button
        color="secondary"
        variant="contained"
        startIcon={
          <AppleIcon sx={{ color: theme.palette.mode === 'dark' ? '#ffffff' : '#000000' }} />
        }
        onClick={handleAppleLogin}
      >
        {t('loginWithApple', 'Login with Apple')}
      </Button>
      <Button
        color="secondary"
        variant="contained"
        startIcon={<GoogleIcon sx={{ color: '#db4437' }} />}
        onClick={handleGoogleLogin}
      >
        {t('loginWithGoogle', 'Login with Google')}
      </Button>
      {error && <Typography color="error" variant="body2" textAlign="center">{error}</Typography>}
    </Stack>
  );
}
